import {action, computed, makeObservable, observable} from 'mobx'
import {injectable} from 'inversify'
import {FormInstance} from 'antd'
import {formatNumberWithSpaces} from '@/pages/onboarding/components/OnboardingAmountFormField/services/utils'

@injectable()
export class AddOutletStore {
    totalAnnualTurnover: number | null = null
    averageTransactionValue: number | null = null
    highestTransactionValue: number | null = null
    form: FormInstance | null = null

    constructor() {
        makeObservable(this, {
            totalAnnualTurnover: observable,
            averageTransactionValue: observable,
            highestTransactionValue: observable,
            form: observable,
            cardAnnualTurnover: computed,
            setTotalAnnualTurnover: action,
            setAverageTransactionValue: action,
            setHighestTransactionValue: action,
            setForm: action,
            resetValues: action,
            updateFormCardAnnualTurnover: action,
            validateHighestTransactionValue: action,
            validateAverageTransactionValue: action
        })
    }

    get cardAnnualTurnover(): number | null {
        if (
            this.totalAnnualTurnover &&
            this.averageTransactionValue &&
            this.averageTransactionValue > 0
        ) {
            const result = this.totalAnnualTurnover / this.averageTransactionValue / 12
            return Math.round(result * 10) / 10
        }
        return null
    }

    setForm(form: FormInstance): void {
        this.form = form
    }

    setTotalAnnualTurnover(value: number | null): void {
        this.totalAnnualTurnover = value
        this.updateFormCardAnnualTurnover()
    }

    setAverageTransactionValue(value: number | null): void {
        this.averageTransactionValue = value
        this.updateFormCardAnnualTurnover()
    }

    setHighestTransactionValue(value: number | null): void {
        this.highestTransactionValue = value
    }

    updateFormCardAnnualTurnover(): void {
        if (this.form) {
            this.form.setFieldsValue({
                cardAnnualTurnover:
                    this.cardAnnualTurnover === null
                        ? null
                        : formatNumberWithSpaces(this.cardAnnualTurnover, {fixedDecimals: 2})
            })
        }
    }

    resetValues(): void {
        this.totalAnnualTurnover = null
        this.averageTransactionValue = null
        this.highestTransactionValue = null
    }

    validateHighestTransactionValue(value: string | number | null): Promise<void> {
        if (!value) return Promise.resolve()

        // Parse the value if it's a formatted string with separators
        const parseFormattedNumber = (val: string | number | null): number | null => {
            if (val === null || val === undefined) return null
            if (typeof val === 'number') return val
            const cleanValue = val.toString().replace(/[\s,]/g, '')
            const numValue = parseFloat(cleanValue)
            return isNaN(numValue) ? null : numValue
        }

        const numericValue = parseFormattedNumber(value)
        if (!numericValue) return Promise.resolve()

        // Get current form values and parse them
        const cardAnnualTurnoverForm = this.form?.getFieldValue('cardAnnualTurnover')
        const averageTransactionValueForm = this.form?.getFieldValue('averageTransactionValue')
        
        const parsedCardAnnualTurnover = parseFormattedNumber(cardAnnualTurnoverForm)
        const parsedAverageTransactionValue = parseFormattedNumber(averageTransactionValueForm)

        if (parsedCardAnnualTurnover && numericValue > parsedCardAnnualTurnover) {
            return Promise.reject(
                new Error('Highest transaction value cannot exceed card annual turnover')
            )
        }

        if (parsedAverageTransactionValue && numericValue < parsedAverageTransactionValue) {
            return Promise.reject(
                new Error(
                    'Highest transaction value must be greater than or equal to average transaction value'
                )
            )
        }

        return Promise.resolve()
    }

    validateAverageTransactionValue(value: string | number | null): Promise<void> {
        if (!value) return Promise.resolve()

        // Parse the value if it's a formatted string with separators
        const parseFormattedNumber = (val: string | number | null): number | null => {
            if (val === null || val === undefined) return null
            if (typeof val === 'number') return val
            const cleanValue = val.toString().replace(/[\s,]/g, '')
            const numValue = parseFloat(cleanValue)
            return isNaN(numValue) ? null : numValue
        }

        const numericValue = parseFormattedNumber(value)
        if (!numericValue) return Promise.resolve()

        // Get current form values and parse them
        const cardAnnualTurnoverForm = this.form?.getFieldValue('cardAnnualTurnover')
        
        const parsedCardAnnualTurnover = parseFormattedNumber(cardAnnualTurnoverForm)

        if (parsedCardAnnualTurnover && numericValue > parsedCardAnnualTurnover) {
            return Promise.reject(
                new Error('Average transaction value cannot exceed card annual turnover')
            )
        }

        return Promise.resolve()
    }
}
